import { animate, style, transition, trigger } from '@angular/animations';
import { DOCUMENT, NgClass, NgOptimizedImage } from '@angular/common';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Inject,
  Input,
  LOCALE_ID,
  OnChanges,
  OnInit,
  Output,
  Renderer2,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { CardProduct } from './../../../core/interfaces/card-product.interface';
import { ExtractBazaarvoiceID } from './../../../shared/pipes/extract-bazaarvoice-id.pipe';

import {
  CartProduct,
  createCartProductFromProduct,
  Metaproduct,
  MetaproductOption,
  Product,
  ProductPrice,
} from '@scalefast/core';

import { SfProductLinkDirective } from '@scalefast/components';
import { StoreSpecificConfigService } from '@scalefast/config-angular';
import { AppAddToCartTemplateComponent } from 'src/app/shared/components/add-to-cart/add-to-cart-template/add-to-cart-template.component';
import { MISSING_PRODUCT_IMAGE_URL } from '../../../app.tokens';
import { AppProductPriceComponent } from '../../../components/product/product-price/product-price.component';
import { AppBazaarvoiceComponent } from '../../../shared/components/bazaarvoice/bazaarvoice.component';
import { AppColorMetaproductComponent } from '../color-metaproduct/color-metaproduct.component';
import { AppSelectYourOptionsComponent } from '../select-your-options/select-your-options.component';

import { CrossSellProductsService, ProductService } from '@scalefast/ecommerce-core';
import { ProductSpecificCampaign } from 'src/app/core/interfaces/page-creator';
import { ProductHelper } from 'src/app/shared/helpers/product.helper';
import { TranslateProductFieldPipe } from 'src/app/shared/pipes/translate-product-field.pipe';
import { ProductSpecificCampaignsService } from 'src/app/shared/services/product-specific-campaigns.service';
import { AppOptionsMetaproductComponent } from '../options-metaproduct/options-metaproduct.component';
import { AppSpecificCampaignHighlightComponent } from '../specific-campaign-highlight/specific-campaign-highlight.component';

@Component({
  imports: [
    RouterModule,
    AppColorMetaproductComponent,
    AppOptionsMetaproductComponent,
    AppSelectYourOptionsComponent,
    AppProductPriceComponent,
    SfProductLinkDirective,
    NgOptimizedImage,
    AppBazaarvoiceComponent,
    AppAddToCartTemplateComponent,
    AppSpecificCampaignHighlightComponent,
    TranslateProductFieldPipe,
    ExtractBazaarvoiceID,
    NgClass,
  ],
  animations: [
    trigger('slideUp', [
      transition(':enter', [
        style({ transform: 'translateY(100%)' }),
        animate('500ms ease-in', style({ transform: 'translateY(0)' })),
      ]),
      transition(':leave', [animate('500ms ease-out', style({ transform: 'translateY(100%)' }))]),
    ]),
  ],
  selector: 'app-product-card',
  standalone: true,
  styleUrls: ['product-card.component.scss'],
  templateUrl: 'product-card.component.html',
  providers: [TranslateProductFieldPipe, ExtractBazaarvoiceID],
})
export class AppProductCardComponent implements OnInit, OnChanges, AfterViewInit {
  @Input({ required: true }) product!: Product;
  @Input() colorReferences: Record<string, string> | undefined = {};
  @Input() displaySpecificProductCampaignHighlights: boolean = false;
  @Input() isSlider: boolean = false;
  @Input() isSlideVisible = false;

  @Output() addToCart = new EventEmitter<Product>();
  @Output() selectYourOptionsEvent = new EventEmitter<Product>(false);

  private allOptions: MetaproductOption[] = [];
  private productTitle: string = '';

  protected productService = inject(ProductService);
  protected crossSellProductsService = inject(CrossSellProductsService);
  protected destroyRef = inject(DestroyRef);

  productPrice: ProductPrice | undefined;
  showFromPriceText: boolean;

  productSpecificCampaign: ProductSpecificCampaign | undefined;
  displayLowestPrice: Boolean = false;

  cardProduct: CardProduct | undefined;
  hasMetaproducts: boolean = false;
  showOptions: boolean = false;
  metaproducts: Metaproduct[] | undefined;
  colorMetaproducts: Metaproduct[] = [];
  optionsMetaproducts: Metaproduct[] = [];
  selectedOptions: Metaproduct[] = [];

  constructor(
    @Inject(LOCALE_ID) protected locale: string,
    @Inject(MISSING_PRODUCT_IMAGE_URL) public missingProductImageUrl: string,
    @Inject(DOCUMENT) private _doc: Document,
    private translateProductFieldPipe: TranslateProductFieldPipe,
    private extractBazaarvoiceIDPipe: ExtractBazaarvoiceID,
    private storeSpecificConfigService: StoreSpecificConfigService,
    private productSpecificCampaignsService: ProductSpecificCampaignsService,
    private el: ElementRef,
    private renderer: Renderer2,
  ) {
    let storeSpecific = this.storeSpecificConfigService.get();
    this.showFromPriceText = storeSpecific.showFromInMetaProductPrices;
  }

  ngOnInit(): void {
    this.productTitle = this.translateProductFieldPipe.transform(this.product, 'title');
    this.hasMetaproducts = this.isMetaproduct;
    this.getOptionsMetaproducts();
    this.metaproducts = this.product.metaproductsV4;
    if (this.metaproducts && this.metaproducts?.length > 0) {
      this.colorMetaproducts = this.metaproducts.filter((metaproduct) => metaproduct.type === 'color');
      this.optionsMetaproducts = this.metaproducts.filter((metaproduct) => metaproduct.type === 'options');
      this.getSelectedOptionsMetaproducts(this.product.id);
    }
    this.setDefaultCartProduct();
    this.getProductSpecificCampaigns();
  }

  ngAfterViewInit() {
    this.updateFocusableElements();
  }

  ngOnChanges() {
    this.updateFocusableElements();
  }

  private updateFocusableElements() {
    if (this.isSlider) {
      const focusableElements = this.el.nativeElement.querySelectorAll('a, button, input, textarea, select');
      focusableElements.forEach((el: HTMLElement) => {
        if (this.isSlideVisible) {
          this.renderer.removeAttribute(el, 'aria-hidden');
          this.renderer.setAttribute(el, 'tabindex', '0');
        } else {
          this.renderer.setAttribute(el, 'aria-hidden', 'true');
          this.renderer.setAttribute(el, 'tabindex', '-1');
        }
      });
    }
  }

  setDefaultCartProduct(): void {
    if (this.product.type == 'META') {
      this.setDefaultCardMetaProduct();
    } else {
      this.setPrice(this.product.id);
      this.cardProduct = {
        id: this.product.id,
        refID: this.product.refID,
        title: this.productTitle,
        image: this.product.imageUrl,
        bVID: this.extractBazaarvoiceIDPipe.transform(this.product, 'Product'),
        stock: this.product.stock.stock,
      };
    }
  }

  setDefaultCardMetaProduct(): void {
    let productID = '';
    const customField = this.product.custom_fields?.find((cf) => cf.type === 'cf_kw_child_preselected');
    let options: MetaproductOption[] = this.allOptions.length > 0 ? this.allOptions : this.colorMetaproducts[0].options;

    if (customField && options.find((o) => o.id == customField.value)) {
      productID = customField.value;
    } else {
      productID = options.sort((a, b) => parseFloat(a.price) - parseFloat(b.price))[0].id;
    }

    this.onColorChange(productID);
  }

  getOptionsMetaproducts() {
    const metaproductOptionsIndex = this.product.metaproductsV4?.findIndex((option) => option.type === 'options');

    // Check if product has options & 2 meta levels
    if (metaproductOptionsIndex !== undefined && metaproductOptionsIndex >= 0 && this.product.metaproductsV4) {
      const { v4ApiResponse, specificV4 } = this.product;
      const children = v4ApiResponse.metaproduct
        ? v4ApiResponse.metaproduct.children
        : specificV4.options.EN.options.flatMap((option: any) => option.options);

      // Map children to update options with new values
      this.allOptions = children.map((child: any) => {
        if (v4ApiResponse.metaproduct) {
          return ProductHelper.processESChildren(child, this.colorReferences);
        } else {
          return ProductHelper.processV4Children(child);
        }
      });

      if (this.product.metaproductsV4?.length > 1) {
        this.product.metaproductsV4[metaproductOptionsIndex].options = this.allOptions;
      }
    }
  }

  getSelectedOptionsMetaproducts(id: string) {
    if (this.optionsMetaproducts.length == 0) {
      return;
    }

    let options = this.allOptions;
    let selectedOptions: MetaproductOption[] = options;

    if (id === this.product.id) {
      selectedOptions = options.filter((obj, index, self) => index === self.findIndex((o) => o.label === obj.label));
    } else {
      const selectedColor = options.find((option) => option.id === id)?.color;
      if (selectedColor) {
        selectedOptions = options.filter((option) => option.color?.toLowerCase() === selectedColor.toLowerCase());
      }
    }
    this.selectedOptions = [
      {
        type: 'options',
        parentProduct: {
          id: this.product.id,
        },
        options: selectedOptions,
      },
    ];
  }

  get currency(): string | undefined {
    return this.product!.price.currency;
  }

  get lang(): string {
    return this.locale.split('-')[0].toUpperCase();
  }

  setCardProduct(id: string) {
    const { v4ApiResponse, specificV4 } = this.product;
    let children = [];
    let currentChild;
    //TODO: Fix when Pandora options info is correct
    if (v4ApiResponse.metaproduct !== undefined) {
      children = v4ApiResponse.metaproduct.children;
      currentChild = children.find((child: any) => child.productID === id);
      const title = this.translateProductFieldPipe.transform(
        { descriptions: currentChild.descriptions, id: currentChild.productID },
        'title',
      );
      this.cardProduct = {
        id: currentChild.productID,
        refID: currentChild.refID,
        image: currentChild.image,
        title: title ?? this.productTitle,
        bVID: currentChild.cf_kw_bvproductid,
        stock: Number(currentChild.stock),
        color: currentChild.platform,
      };
    } else {
      children = specificV4.options.EN.options.flatMap((option: any) => option.options);
      currentChild = children.find((child: any) => child.productID === id);
      const selectedColor = currentChild.custom_fields.find((field: any) => field.type === 'CF_PLATFORM');
      this.cardProduct = {
        id: currentChild.productID,
        refID: '',
        image: this.product.imageUrl,
        title: currentChild.product_title ?? this.productTitle,
        bVID: currentChild.custom_fields.find((field: any) => field.type === 'CF_BVPRODUCTID').value,
        stock: Number(currentChild.stock_policy),
        color: selectedColor ? selectedColor.value : '',
      };
    }

    this.getProductSpecificCampaigns();
  }

  setPrice(id: string) {
    const { price, v4ApiResponse } = this.product;

    // If product is a Metaproduct get price following these conditions
    if (this.isMetaproduct && v4ApiResponse?.metaproduct?.children) {
      const children = v4ApiResponse.metaproduct.children;
      let priceChild;

      if (id === this.product.id) {
        // Find the child with the lowest price
        priceChild = children
          .flatMap((child: any) => child.prices.filter((price: any) => price.bundle_object_id === '-1'))
          .sort((a: any, b: any) => a.price - b.price)[0];
      } else {
        // Find the price of the children with the selected id
        const currentChild = children.find((child: any) => child.productID === id);
        priceChild = currentChild.prices.find((price: any) => price.bundle_object_id === '-1');
      }

      if (priceChild) {
        const currencyPrice = price.currencies.find((currency) => currency.currency === this.currency);

        if (currencyPrice && currencyPrice.prices?.[0]) {
          // Set current price and old price for the product currency
          Object.assign(currencyPrice.prices[0], {
            price: priceChild.price,
            oldPrice: priceChild.crossed_price,
            recommendedPrice: priceChild.recommended_price,
          });
        }
      }

      this.productPrice = price;
    } else {
      // If product is not a Metaproduct, use the product's original price
      this.productPrice = price;
    }
  }

  onAddToCart(id: string): void {
    if (this.isMetaproduct) {
      this.onGetProductById(id);
    } else {
      this.addToCart.emit(this.product);
    }
    this.setShowOptions(false);
  }

  onColorChange(productId: string): void {
    this.onOptionChange(productId);
    this.getSelectedOptionsMetaproducts(productId);
  }

  onOptionChange(productId: string): void {
    this.setCardProduct(productId);
    this.setPrice(productId);
    this.showFromPriceText = false;
  }

  onGetProductById(productId: string): void {
    this.productService.getProduct$(parseInt(productId)).subscribe((data) => this.addToCart.emit(data));
  }

  onSelectYourOptions(product: Product): void {
    this.selectYourOptionsEvent.emit(product);
  }

  setShowOptions(value: boolean) {
    this.showOptions = value;
    this._doc.body.classList.toggle('options-open', value);
  }

  transformProductToCartProduct(product: Product): CartProduct {
    const cProduct = createCartProductFromProduct(product);
    if (this.product.type === 'META' && this.cardProduct) {
      cProduct.stock = this.cardProduct.stock;
    }
    return cProduct;
  }

  getProductSpecificCampaigns() {
    if (this.cardProduct)
      this.productSpecificCampaignsService
        .getProductSpecificCampaigns(this.cardProduct.id, this.cardProduct.refID ?? '')
        .subscribe((productSpecificCampaigns) => {
          this.productSpecificCampaign = productSpecificCampaigns[0];
          this.displayLowestPrice = true;
        });
  }

  get isMetaproduct(): boolean {
    return this.product!.type === 'META';
  }

  get productFeeds() {
    //FIXME: memoize this, avoid v4ApiResponse, probably also the feature is a mistake
    return this.product!.v4ApiResponse?.feeds?.map((feed: any) => feed.feedID);
  }
}
